/**
 * Audio Device Utilities
 *
 * Reusable device enumeration and resolution for audio features.
 * Used by idle stream and future recording system.
 */

import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';

import { desktopAudioRecorderBridge } from './index';
import type { AudioDevice, AvailableDevices } from './types';

/** Device error messages */
export const DEVICE_MESSAGES = {
  NO_INPUT_DEVICES: 'No audio input devices found.',
  NO_OUTPUT_DEVICES: 'No audio output devices found.',
  DEVICE_NOT_FOUND: (name: string) => `Audio device not found: ${name}`,
} as const;

/**
 * Get all available audio devices (both input and output) in a single call
 *
 * @returns AvailableDevices with input_devices and output_devices
 * @throws Error if enumeration fails
 */
export async function getAvailableDevices(): Promise<AvailableDevices> {
  return desktopAudioRecorderBridge.getAvailableDevices();
}

/**
 * Get all available devices and ensure at least one input device exists
 *
 * @returns AvailableDevices (guaranteed non-empty input_devices)
 * @throws Error if no input devices found
 */
export async function ensureAvailableDevices(): Promise<AvailableDevices> {
  const devices = await getAvailableDevices();

  if (!devices.input_devices?.length) {
    throw new Error(DEVICE_MESSAGES.NO_INPUT_DEVICES);
  }

  return devices;
}

/**
 * Resolve device identifier to actual device ID
 *
 * Handles:
 * - undefined/SYSTEM_DEFAULT_DEVICE_MARKER -> returns undefined (use system default)
 * - Direct ID match -> returns the ID
 * - Name match -> returns the device's ID
 *
 * @param requested - Device ID or name requested by user
 * @param devices - List of available devices
 * @returns Resolved device ID or undefined for system default
 * @throws Error if device not found
 */
export function resolveDeviceId(
  requested: string | undefined,
  devices: AudioDevice[]
): string | undefined {
  // Use system default
  if (!requested || requested === SYSTEM_DEFAULT_DEVICE_MARKER) {
    return undefined;
  }

  // Try direct ID match first
  const byId = devices.find((d) => d.id === requested);
  if (byId) return byId.id;

  // Fallback: match by name
  const byName = devices.find((d) => d.name === requested);
  if (byName) return byName.id;

  throw new Error(DEVICE_MESSAGES.DEVICE_NOT_FOUND(requested));
}

/**
 * Resolve output device identifier to actual device ID
 * (Alias for resolveDeviceId - same logic for both input and output)
 */
export const resolveOutputDeviceId = resolveDeviceId;

/**
 * Find default device from list
 *
 * @param devices - List of available devices
 * @returns Default device or first device if no default marked
 */
export function findDefaultDevice(devices: AudioDevice[]): AudioDevice | undefined {
  return devices.find((d) => d.is_default) ?? devices[0];
}

/**
 * Find default output device from list
 * (Alias for findDefaultDevice - same logic for both input and output)
 */
export const findDefaultOutputDevice = findDefaultDevice;
